import { desanitizeKey } from '@/helper/utils';
import { AppDispatch, RootState } from '@/store';
import {
  clearSelectedProject,
  fetchProjectsList,
} from '@/store/slices/projectsSlice/projectsSlice';
import { Project } from '@/types/project.types';
import {
  BellRing,
  ChevronDown,
  ChevronUp,
  Clipboard,
  Columns3,
  FolderClosed,
  HelpCircle,
  LogOut,
  Menu,
  Shield,
  Users,
  WalletCards,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Dashboard from './Dashboard';
import FAQs from './FAQs/FAQs';
import NotificationManagement from './NotificationManagement/NotificationManagement';
import Projects from './Projects/Projects';
import SiteManagement from './SiteManagement/SiteManagement';
import SurveyManagement from './SurveyManagement/SurveyManagement';
import SurveySetManagement from './SurveySetManagement/SurveySetManagement';
import SystemAdminUsers from './SystemAdminUsers/SystemAdminUsers';
import WalletComp from './WalletManagement/WalletManagement';

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  disabled?: boolean;
  component: React.ElementType;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard Overview',
    icon: Columns3,
    href: '/dashboard',
    component: Dashboard,
  },
  {
    label: 'System Users',
    icon: Shield,
    href: '/dashboard/system-admin-users',
    component: SystemAdminUsers,
  },
  {
    label: 'Project Management',
    icon: FolderClosed,
    href: '/dashboard/project-management',
    component: Projects,
  },
  {
    label: 'Site Management',
    icon: FolderClosed,
    href: '/dashboard/site-management',
    component: SiteManagement,
  },
  {
    label: 'Survey Management',
    icon: Users,
    href: '/dashboard/survey-management',
    component: SurveyManagement,
  },
  {
    label: 'Survey-Set Management',
    icon: Clipboard,
    href: '/dashboard/surveyset-management',
    component: SurveySetManagement,
  },
  {
    label: 'Wallet Management',
    icon: WalletCards,
    href: '/dashboard/wallet-management',
    component: WalletComp,
  },
  {
    label: 'Notification Management',
    icon: BellRing,
    href: '/dashboard/notification-management',
    component: NotificationManagement,
  },

  { label: 'FAQs', icon: HelpCircle, href: '/dashboard/faqs', component: FAQs },
];

const PROJECTS_PER_PAGE = 3;

export const SideBar: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const dispatch = useDispatch<AppDispatch>();

  const [active, setActive] = useState<string>(() => {
    // Restore from localStorage, fallback to matching current URL, then first nav item
    const saved = localStorage.getItem('sidebarActive');
    if (saved) return saved;
    const currentPath = window.location.pathname;
    const matched = navItems.find((item) => currentPath.includes(item.href));
    return matched ? matched.label : navItems[0].label;
  });

  const setActivePersisted = (label: string) => {
    localStorage.setItem('sidebarActive', label);
    setActive(label);
  };

  // Set active nav item based on current URL for survey sub-routes
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (
      currentPath.includes('/survey-management') ||
      currentPath.includes('/create-survey') ||
      currentPath.includes('/edit-survey')
    ) {
      setActivePersisted('Survey Management');
    }
  }, []);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  const { list, loading } = useSelector((state: RootState) => state.projects);
  const projects = list.data || [];
  const currentPage = list.page || 1;
  const totalPages = list.totalPages || 1;

  useEffect(() => {
    if (projectDropdownOpen && projects.length === 0) {
      dispatch(fetchProjectsList({ page: 1, limit: PROJECTS_PER_PAGE }));
    }
  }, [projectDropdownOpen, dispatch, projects.length]);

  const handleLoadMore = () => {
    if (currentPage < totalPages && !loading) {
      dispatch(fetchProjectsList({ page: currentPage + 1, limit: PROJECTS_PER_PAGE }));
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project.id);
    dispatch(clearSelectedProject());
    navigate('/dashboard/project-management/IndividualProject', {
      state: { project: project },
    });
    setSidebarOpen(false);
  };

  const handleItemClick = (item: NavItem) => {
    // if (item.label === 'System Users') {
    //   setSystemUsersActive(!systemUsersActive);
    // } else {
    // setSystemUsersActive(false);
    // }
    setProjectDropdownOpen(false);
    setOpenDropdown(null);
    if (item.children) {
      setOpenDropdown(openDropdown === item.label ? null : item.label);
    } else {
      setActivePersisted(item.label);
      navigate(item.href);
      setSidebarOpen(false);
    }
  };

  const hasMoreProjects = currentPage < totalPages;

  const roleName = (() => {
    try {
      const storedRole = localStorage.getItem('roleInfo');
      return storedRole ? JSON.parse(storedRole)?.name : undefined;
    } catch {
      return undefined;
    }
  })();

  // const blueNumberId = (() => {
  //   try {
  //     const stored = localStorage.getItem('userResponse');
  //     if (!stored) return undefined;

  //     const parsed = JSON.parse(stored);
  //     return parsed?.user?.blueNumber;
  //   } catch (error) {
  //     console.error('Failed to parse userResponse from localStorage', error);
  //     return undefined;
  //   }
  // })();

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-pink-800 flex items-center justify-between p-3 z-50 shadow-md">
        <h1 className="text-white font-bold text-lg">MillionMakers™</h1>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
        ></div>
      )}

      {/* Sidebar */}
      <aside
        style={{
          background: 'linear-gradient(174.07deg, #AD0C62 0.38%, #621C43 95.87%)',
        }}
        className={`w-64 text-white flex flex-col justify-between shadow-lg
          md:relative md:h-auto
          fixed md:translate-x-0 top-0 left-0 h-full z-50 transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        <div className="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 py-3.5 font-bold text-lg border-b border-[#FFFFFF] md:flex hidden">
            <div>
              <h1 className="text-xl font-bold text-white">MillionMakers™</h1>
              <p className="text-xl text-white mb-1">Admin Portal</p>
            </div>
            <img
              src="/images/logo.png"
              alt="Admin Portal Logo"
              className="w-12 h-12 object-contain"
            />
          </div>

          {/* Mobile Header Inside Sidebar */}
          <div className="md:hidden flex justify-between items-center p-4 border-b border-[0.5px] border-[#FFFFFF]">
            <div>
              <h1 className="text-xl font-bold text-white">MillionMakers™</h1>
              <p className="text-sm text-white">Admin Portal</p>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="text-white">
              <X size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="space-y-0">
            {navItems.map((item) => {
              const isActive = active === item.label;
              const isOpen = openDropdown === item.label;
              const isBeforeProjectManagement = item.label === 'Project Management';

              return (
                <div key={item.label}>
                  {/* Project Section */}
                  {isBeforeProjectManagement && (
                    <div>
                      <button
                        onClick={() => {
                          setProjectDropdownOpen(!projectDropdownOpen);
                          setOpenDropdown(null);
                        }}
                        className="flex items-center justify-between w-full px-4 py-3 font-medium text-left border-b border-[#aaa9a9] transition duration-300 bg-[#ffffff22] hover:bg-[#ffffff33] text-white backdrop-blur-sm"
                      >
                        <div className="flex justify-between items-center w-full pb-1 border-b-[0.5px] border-[##FAF8F9] border-opacity-5">
                          <div className="flex items-center gap-3">
                            <span className="italic">Select Project</span>
                          </div>
                          {projectDropdownOpen ? (
                            <ChevronUp size={24} color="#fff" strokeWidth={1} />
                          ) : (
                            <ChevronDown size={24} color="#fff" strokeWidth={1} />
                          )}
                        </div>
                      </button>

                      {projectDropdownOpen && (
                        <div className="bg-[#ffffff1a] border-b border-[#aaa9a9] backdrop-blur-sm">
                          {/* Scrollable Projects Container */}
                          <div
                            className="overflow-y-auto space-y-1 px-3 py-2 scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent hover:scrollbar-thumb-white/50 scrollbar-thumb-rounded-full"
                            style={{
                              maxHeight: '156px',
                              minHeight: '156px',
                            }}
                          >
                            {loading && projects.length === 0 ? (
                              <div className="text-center py-4 text-white/70 text-sm">
                                Loading projects...
                              </div>
                            ) : projects.length === 0 ? (
                              <div className="text-center py-4 text-white/70 text-sm">
                                No projects available
                              </div>
                            ) : (
                              <>
                                {projects.map((proj) => (
                                  <button
                                    key={proj.id}
                                    onClick={() => handleProjectSelect(proj)}
                                    className={`w-full text-left text-sm px-3 py-2 rounded transition duration-200 
                                      ${
                                        selectedProject === proj.id
                                          ? 'bg-white text-[#AD0C62] font-semibold'
                                          : 'text-white/90 hover:text-white hover:bg-[#ffffff33]'
                                      }`}
                                  >
                                    {proj.name}
                                  </button>
                                ))}
                              </>
                            )}
                          </div>

                          {/* Load More Button */}
                          {hasMoreProjects && (
                            <div className="px-3 pb-3 pt-1">
                              <button
                                onClick={handleLoadMore}
                                disabled={loading}
                                className="w-full text-center text-xs px-3 py-1.5 rounded-md transition-all duration-200 bg-gradient-to-r from-[#AD0C62] to-[#621C43] hover:from-[#c20e6e] hover:to-[#6e1f48] text-white font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed border border-white/20"
                              >
                                {loading ? (
                                  <span className="flex items-center justify-center gap-1">
                                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        fill="none"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                    Loading...
                                  </span>
                                ) : (
                                  'Load More'
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Main Button */}
                  <button
                    onClick={() => handleItemClick(item)}
                    className={`flex items-center gap-3 w-full px-4 py-3 border-[#fff] border-b-[0.5px] font-medium text-[14px] text-left transition-all duration-300 
                       ${
                         isActive
                           ? 'bg-white text-[#AD0C62] font-semibold shadow-md'
                           : 'text-white hover:text-white hover:bg-[#ffffff22] opacity-45'
                       }
                    ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    disabled={item.disabled}
                  >
                    {React.createElement(item.icon, { size: 18 })}
                    {item.label}
                  </button>

                  {/* Dropdown */}
                  {item.children && isOpen && (
                    <div className="mt-1 space-y-1 w-full px-3">
                      {item.children.map((child) => (
                        <button
                          key={child.label}
                          onClick={() => {
                            setActivePersisted(child.label);
                            navigate(child.href);
                            setSidebarOpen(false);
                          }}
                          className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left rounded transition-all duration-200
                              ${
                                active === child.label
                                  ? 'bg-white text-[#AD0C62] font-semibold'
                                  : 'text-white/70 hover:text-white hover:bg-[#ffffff22]'
                              }`}
                        >
                          {React.createElement(child.icon, { size: 16 })}
                          {child.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Footer */}
        <div className="p-4 border-t-[0.5px] border-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <img
              src="/admin-icon.png"
              alt="Admin"
              className="w-10 h-10 rounded-full border-1 object-cover"
            />

            <div className="flex flex-col whitespace-nowrap max-w-full">
              <p className="font-bold flex-1 min-w-0 text-[clamp(7px,1vw,22px)] truncate">
                {desanitizeKey(roleName)}
              </p>
              {/* <p className="text-sm shrink-0">{blueNumberId}</p> */}
            </div>
          </div>

          <button
            onClick={() => signOut()}
            className="flex items-center gap-2 w-full mt-3 px-2 py-2 text-sm text-left hover:bg-white hover:text-[#AD0C62] rounded transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

export default SideBar;
